import React from "react";
import { useSelector, useDispatch } from "react-redux";

import {
    Button,
    Typography,
    Card,
    Checkbox,
    Input,
} from "@material-tailwind/react";

import { Search, Refresh, User } from "iconoir-react";
import {
    Employee,
    listEmployeesRequest,
} from "../../../redux/employee/actions";
import { RootState } from "../../../redux";
import Loader from "../../../components/Loader";


const TABLE_HEAD = [
    { n: "Name", className: "w-48" },
    { n: "Email", className: "" },
    { n: "Listed Vehicles", className: "" },
    { n: "Draft Vehicles", className: "" },
];

const DashboardClients = () => {
    const { clients, loading } = useSelector((state: RootState) => state.employees);
    const dispatch = useDispatch();
    const [search, setSearch] = React.useState<string>("");
    const [filterEmployees, setFilterEmployees] = React.useState<Employee[]>([]);
    const [selectedEmployees, setSelectedEmployees] = React.useState<number[]>([]);

    const handleRefresh = () => {
        dispatch(
            listEmployeesRequest({
                filters: "user__is_staff:False",
                members_fields: [
                    "id",
                    "user",
                    "avatar",
                    "vehicles_listed",
                    "vehicles_drafts",
                ].join(","),
                excludes: "",
                save_as: "clients",
            })
        );
    };

    React.useEffect(() => {
        const handleFilterApply = () => {
            let result = [...clients];

            if (search) {
                result = result.filter((el) => {
                    const searchString = `${el?.user?.first_name} ${el?.user?.last_name} ${el?.user?.email}`;
                    return searchString.toLowerCase().includes(search.toLowerCase());
                });
            }

            setFilterEmployees(result);
        };

        handleFilterApply();
    }, [clients, search]);

    React.useEffect(() => {
        handleRefresh();
    }, [dispatch]);

    const handleCheckboxChange = (id: number) => {
        setSelectedEmployees((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    return (
        <div className="flex flex-col gap-y-5 mt-5 text-black w-full">
            <Card>
                <Card.Header className="w-full m-0 items-start rounded-none flex flex-wrap gap-4 justify-between p-4">
                    <div>
                        <Typography type="lead" color="default" className="font-semibold mb-1">
                            Employees
                        </Typography>
                        <Typography type="small" className="text-foreground block">
                            Here you have details about the company
                        </Typography>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center w-full shrink-0 gap-3 md:w-max">
                        <Input
                            placeholder="Search here..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full sm:w-72"
                        >
                            <Input.Icon>
                                <Search className="w-full h-full" />
                            </Input.Icon>
                        </Input>
                        <div className="flex gap-3 sm:w-max w-full">
                            <Button
                                variant="outline"
                                color="secondary"
                                className="flex items-center gap-2 sm:w-auto w-full"
                                onClick={() => {
                                    handleRefresh();
                                }}
                            >
                                <Refresh className="w-4 h-4" />
                            </Button>
                         </div>
                    </div>
                </Card.Header>
                <Card.Body className="mt-4 p-0 rounded-none max-h-[calc(100vh-240px)] overflow-y-auto scrollbar-hide">
                    <table className="w-full min-w-max table-auto">
                        <thead>
                            <tr>
                                {TABLE_HEAD.map((el, idx: number) => (
                                    <th
                                        key={idx}
                                        className={`sticky top-0 bg-white z-10 border-b text-start border-surface p-4 ${el.className}`}
                                    >
                                        {idx == 0 ? (
                                            <div className="flex items-center gap-5">
                                                <Checkbox
                                                    id="checkbox_all"
                                                    key={`${Math.random()}`}
                                                    checked={
                                                        selectedEmployees.length ==
                                                        filterEmployees.length
                                                    }
                                                    onChange={() => {
                                                        if (
                                                            selectedEmployees.length ==
                                                            filterEmployees.length
                                                        ) {
                                                            setSelectedEmployees([]);
                                                        } else {
                                                            setSelectedEmployees(
                                                                filterEmployees.map(
                                                                    (el) => el.id || 0
                                                                )
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <Checkbox.Indicator />
                                                </Checkbox>
                                                <Typography
                                                    type="small"
                                                    color="default"
                                                    className="block font-medium"
                                                >
                                                    {el.n}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="block font-medium"
                                            >
                                                {el.n}
                                            </Typography>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filterEmployees?.map((el: Employee) => (
                                <tr
                                    key={el.id}
                                    onClick={(e) => {
                                        if (
                                            (e.target as HTMLElement).tagName === "INPUT" ||
                                            (e.target as HTMLElement).tagName === "IMG" ||
                                            (e.target as HTMLElement).tagName === "LABEL"
                                        ) {
                                            return;
                                        }
                                        return;
                                    }}
                                >
                                    <td className="px-4 border-b border-surface">
                                        <div className="flex items-center gap-5">
                                            <Checkbox
                                                key={`${el.id}_${Math.random()}`}
                                                id={`checkbox_${el?.id || 0}`}
                                                checked={selectedEmployees.includes(el?.id || 0)}
                                                onChange={() => handleCheckboxChange(el?.id || 0)}
                                            >
                                                <Checkbox.Indicator />
                                            </Checkbox>
                                            {el.avatar ? (
                                                <div className="flex items-center justify-center w-7 h-7 bg-gray-200 rounded-full">
                                                    <img
                                                        src={el.avatar}
                                                        alt="avatar"
                                                        className="h-5 w-5 rounded-full"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center w-7 h-7 bg-gray-200 rounded-full">
                                                    <User className="h-5 w-5" />
                                                </div>
                                            )}
                                            <Typography
                                                type="small"
                                                color="default"
                                                className="py-4 overflow-hidden flex items-center text-ellipsis w-48 !text-nowrap text-foreground font-medium"
                                            >
                                                {el.user?.first_name} {el.user?.last_name}
                                            </Typography>
                                        </div>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el.user?.email}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el?.vehicles_listed}
                                        </Typography>
                                    </td>
                                    <td className="p-4 border-b border-surface">
                                        <Typography type="small" className="block text-foreground">
                                            {el?.vehicles_drafts}
                                        </Typography>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card.Body>
            </Card>

            {loading && (
                <div className="flex justify-center items-center h-full">
                    <Loader />
                </div>
            )}
        </div>
    );
};

export default DashboardClients;
